/** Session metadata lives in the person's World when sharing is enabled. */
import type { DocHandle, Repo } from '@automerge/automerge-repo/slim'
import { findProjectForWrite } from '../project/mutations'
import { changeProject } from '../project/scalars'
import type { WorldDoc } from '../world/types'
import type { createSession } from './mutations'
import type { SessionDoc, SessionMetaEntry } from './types'

type CatalogDraft = {
  sessionIds?: string[]
  sessionMeta?: Record<string, SessionMetaEntry>
}
const privateRepos = new WeakSet<Repo>()
export function requirePrivateSessionCatalog(repo: Repo): void {
  privateRepos.add(repo)
}
export function usesPrivateSessionCatalog(repo: Repo): boolean {
  return privateRepos.has(repo) || privateCatalogs.has(repo)
}
const privateCatalogs = new WeakMap<Repo, DocHandle<WorldDoc>>()
const projectChecks = new WeakMap<Repo, (projectId: string) => Promise<void>>()

/** Discovery only; the sharing repo still enforces the signed document grant. */
export async function sharedSessionEntry(repo: Repo, sessionId: string) {
  const world = privateCatalogs.get(repo)
  if (!world) return undefined
  await world.whenReady()
  if (world.doc().privateSessions?.[sessionId]) return undefined
  const entry = world.doc().sharedWithMe?.[sessionId]
  return entry && String(entry.kind) === 'session' ? entry : undefined
}

/** Bind only after the sharing layer verifies the person's World grant. */
export function registerPrivateSessionCatalog(
  repo: Repo,
  world: DocHandle<WorldDoc>,
  checkProject: (projectId: string) => Promise<void>,
): void {
  const existing = privateCatalogs.get(repo)
  if (existing && existing.documentId !== world.documentId)
    throw new Error('A repo cannot switch private session catalogs')
  privateCatalogs.set(repo, world)
  projectChecks.set(repo, checkProject)
  for (const listener of catalogListeners.get(repo) ?? []) listener()
}

export async function findSessionCatalogForWrite(
  repo: Repo,
  projectId: string,
  sessionId: string,
): Promise<{
  change: (write: (draft: CatalogDraft) => void) => void
}> {
  const world = privateCatalogs.get(repo)
  if (world) {
    await world.whenReady()
    const entry = world.doc().privateSessions?.[sessionId]
    if (!entry || entry.projectId !== projectId)
      throw new Error('Session is not in this private project catalog')
    return {
      change(write) {
        world.change((d) => {
          const entry = d.privateSessions?.[sessionId]
          if (!entry || entry.projectId !== projectId)
            throw new Error('Session is not in this private project catalog')
          write({ sessionMeta: d.privateSessions })
        })
      },
    }
  }
  if (privateRepos.has(repo))
    throw new Error('Private session catalog is not configured')
  const project = await findProjectForWrite(repo, projectId)
  if (project.doc().sharingGroupId)
    throw new Error('Private session catalog is not configured')
  // A project registry stores one-op strings (RFC 0357 S11); the private
  // World catalog above keeps its own encoding.
  return { change: (write) => changeProject(project, write) }
}

/** Moving a private session changes only its private association, never grants. */
export async function movePrivateSessionCatalog(
  repo: Repo,
  sessionId: string,
  fromProjectId: string,
  toProjectId: string,
): Promise<void> {
  const world = privateCatalogs.get(repo)
  const checkProject = projectChecks.get(repo)
  if (!world || !checkProject)
    throw new Error('Private session catalog is not configured')
  await world.whenReady()
  await checkProject(toProjectId)
  world.change((d) => {
    const entry = d.privateSessions?.[sessionId]
    if (!entry || entry.projectId !== fromProjectId)
      throw new Error('Session is not in this private project catalog')
    entry.projectId = toProjectId
  })
}

type PrivateSessionCreator = (
  projectId: string,
  options: Parameters<typeof createSession>[2],
) => Promise<DocHandle<SessionDoc>>
const privateCreators = new WeakMap<Repo, PrivateSessionCreator>()
export function registerPrivateSessionCreator(
  repo: Repo,
  create: PrivateSessionCreator,
): void {
  privateCreators.set(repo, create)
}
export function createCatalogSession(
  repo: Repo,
  projectId: string,
  options: Parameters<typeof createSession>[2],
): Promise<DocHandle<SessionDoc>> {
  const create = privateCreators.get(repo)
  if (!create) throw new Error('Private session catalog is not configured')
  return create(projectId, options)
}

const catalogListeners = new WeakMap<Repo, Set<() => void>>()
export function privateSessionWorldId(repo: Repo): string | undefined {
  return privateCatalogs.get(repo)?.documentId
}
export function subscribePrivateSessionCatalog(
  repo: Repo,
  listener: () => void,
): () => void {
  let listeners = catalogListeners.get(repo)
  if (!listeners) {
    listeners = new Set()
    catalogListeners.set(repo, listeners)
  }
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** RFC 0345: the sessions this person's private catalog files under one
 * project, with their run status. A sharing World keeps them here and never
 * in the protected project's registry, so this is what a host drains. */
export function privateCatalogSessions(
  repo: Repo,
  projectId: string,
): Record<string, SessionMetaEntry> {
  const out: Record<string, SessionMetaEntry> = {}
  const entries = privateCatalogs.get(repo)?.doc()?.privateSessions ?? {}
  for (const [id, entry] of Object.entries(entries))
    if (entry && String(entry.projectId) === projectId) out[id] = entry
  return out
}

/** Calls `onChange` whenever the private catalog changes, including when one
 * is bound after this was called. */
export function watchPrivateCatalog(
  repo: Repo,
  onChange: () => void,
): () => void {
  let world: DocHandle<WorldDoc> | undefined
  const attach = () => {
    const next = privateCatalogs.get(repo)
    if (!next || next === world) return
    world?.off('change', onChange)
    world = next
    world.on('change', onChange)
    onChange()
  }
  const unsubscribe = subscribePrivateSessionCatalog(repo, attach)
  attach()
  return () => {
    unsubscribe()
    world?.off('change', onChange)
  }
}

/** RFC 0345: the project a person's private catalog files a session under,
 * or undefined when no catalog is bound or it does not list the session. */
export function privateCatalogProjectOf(
  repo: Repo,
  sessionId: string,
): string | undefined {
  const entry = privateCatalogs.get(repo)?.doc()?.privateSessions?.[sessionId]
  const projectId = entry ? String(entry.projectId ?? '') : ''
  return projectId || undefined
}
